import React, { useMemo, useState } from 'react'

import { useIntl } from 'react-intl'
import { captureException } from '@sentry/react'
import { TableColumn } from 'react-data-table-component'
import { LoanRole, UserLoan } from '@matech/thebigpos-sdk'

import useShareLoan, {
	LOAN_ROLES_TO_EXCLUDE_FROM_SHARED_USERS,
} from '@/hooks/useShareLoan'
import { UserRole } from '@/types'
import { useAlert } from '@/hooks'
import { getTheme } from '@/config'
import useUser from '@/hooks/useUser'
import { applyMask } from '@/services/utils'
import { formatDate } from '@/services/helper'
import useLoanInvites from '@/hooks/useLoanInvites'
import { useLoanContext } from '@/contexts/LoanContext'
import { addSpacesToPascalCase } from '@/services/helpers'
import ModalShareLoan from '@/components/modals/ModalShareLoan'
import DataTable, { type ActionItem } from '@/components/DataTable'
import type { SelectChangeEvent } from '@/components/formControls/Select'
import ModalShareLoanRevoke from '@/components/modals/ModalShareLoanRevoke'
import type { LoanUserRow } from '@/components/loanAppDetails/loan-users-table/types'

import LoanUsersTablesActionsButton from './LoanUsersTablesTitleActions'
import LoanUsersTableRoleAssignCell from './LoanUsersTableRoleAssignCell'

const theme = getTheme()

export type LoanUsersTableProps = {
	title?: string
}

type EditingRowState = {
	role: LoanRole
	loading: boolean
}

type EditingRowMap = Map<string, EditingRowState>

const DEFAULT_SORT_COLUMN = 'createdAt'
const ALLOWED_ROLES_INVITE = new Set(['Borrower', 'CoBorrower'])
const DEFAULT_PAGE_NUMBER = 1
const DEFAULT_PAGE_SIZE = 1000

const mapUserLoanListToDataRowList = (userLoans?: UserLoan[]) => {
	return (
		userLoans
			?.map(
				(userLoan) =>
					({
						id: userLoan.user.id ?? '',
						name: `${userLoan.user.firstName} ${userLoan.user.lastName}`,
						email: userLoan.user.email ?? '',
						phone: userLoan.user.phone,
						userRole: userLoan.user.role,
						role: userLoan.role || '',
						createdAt: formatDate(userLoan.user.createdAt, false),
						shared: !LOAN_ROLES_TO_EXCLUDE_FROM_SHARED_USERS.includes(
							userLoan.role
						),
					}) as LoanUserRow
			)
			?.sort((a, b) => a.name.localeCompare(b.name)) || []
	)
}

const LoanUsersTable = ({ title }: LoanUsersTableProps) => {
	const { dataTable: breakpoint } = theme.breakpoints

	const {
		loan,
		loanId,
		refetch,
		isLoading: isLoanLoading,
		isRefetching: isLoanRefetching,
	} = useLoanContext()
	const { alert } = useAlert()
	const { formatMessage } = useIntl()
	const { user, isLoanAdmin, isBorrower } = useUser()

	const [userToRevoke, setUserToRevoke] = useState<
		LoanUserRow | undefined
	>(undefined)
	const [isRevokeModalVisible, setIsRevokeModalVisible] =
		useState(false)
	const [isShareLoanModalVisible, setIsShareLoanModalVisible] =
		useState(false)
	const [isModalSubmitting, setIsModalSubmitting] = useState(false)
	const [editingRows, setEditingRows] = useState<EditingRowMap>(
		new Map()
	)

	const { sendInviteReminderMutation } = useLoanInvites(loanId)

	const {
		addSharedUserMutation,
		revokeSharedUserMutation,
		updateSharedUserRole,
	} = useShareLoan({
		loanId,
		enabledQuery: false,
		paginationParams: {
			pageNumber: DEFAULT_PAGE_NUMBER,
			pageSize: DEFAULT_PAGE_SIZE,
		},
	})

	const memoMergedData = useMemo(() => {
		const loanOfficerList: LoanUserRow[] = loan?.loanOfficer
			? [
					{
						id: loan.loanOfficer.id,
						name: loan.loanOfficer.name ?? '',
						email: loan.loanOfficer.email,
						phone: loan.loanOfficer.phone,
						role: 'LoanOfficer',
						createdAt:
							loan.loanOfficer.siteConfiguration?.createdAt ?? '',
					},
				]
			: []

		const loanUserList = mapUserLoanListToDataRowList(loan?.userLoans)

		return [...loanOfficerList, ...loanUserList]
	}, [loan])

	const handleRevokeClick = (row: LoanUserRow) => {
		setUserToRevoke(row)
		setIsRevokeModalVisible(true)
	}

	const handleCloseRevokeModal = () => {
		setIsRevokeModalVisible(false)
		setUserToRevoke(undefined)
	}

	const handleRoleSaveRow = async (row: LoanUserRow) => {
		const userId = row.id
		const editingRow = editingRows.get(userId)

		if (!editingRow) return

		const role = editingRow?.role

		setEditingRows((prev) => {
			const newMap = new Map(prev)
			const current = newMap.get(userId)
			if (current) newMap.set(userId, { ...current, loading: true })
			return newMap
		})

		try {
			await updateSharedUserRole({
				userId,
				role,
				updateCache: false,
				showToast: false,
			})

			alert(
				formatMessage(
					{ id: 'alerts.role_update_success' },
					{ name: row.name }
				),
				{ severity: 'success' }
			)

			setEditingRows((prev) => {
				const newMap = new Map(prev)
				newMap.delete(userId)
				return newMap
			})

			await refetch?.()
		} catch (error) {
			captureException(error)
			alert(formatMessage({ id: 'alerts.role_update_failed' }), {
				severity: 'error',
			})
			setEditingRows((prev) => {
				const newMap = new Map(prev)
				const current = newMap.get(userId)
				if (current)
					newMap.set(userId, { ...current, loading: false })
				return newMap
			})
		}
	}

	const handleCancelEditRow = (row: LoanUserRow) => {
		setEditingRows((prev) => {
			const newMap = new Map(prev)
			newMap.delete(row.id)
			return newMap
		})
	}

	const handleRoleChangeRow = (
		e: SelectChangeEvent<unknown>,
		rowId: string
	) => {
		setEditingRows((prev) => {
			const newMap = new Map(prev)
			const current = newMap.get(rowId)
			newMap.set(rowId, {
				role: e.target.value as LoanRole,
				loading: !!current?.loading,
			})
			return newMap
		})
	}

	const handleOpenShareLoanModal = () => {
		setIsShareLoanModalVisible(true)
	}

	const handleCloseShareLoanModal = () => {
		setIsShareLoanModalVisible(false)
	}

	const handleValidateUserBeforeAdd = (data: LoanUserRow) => {
		const userIndex = memoMergedData.findIndex(
			(item) => item.id === data.id
		)

		if (userIndex < 0) return true

		alert(
			formatMessage({
				id: 'share_loan.validation.user_already_added',
			}),
			{
				severity: 'error',
			}
		)

		return false
	}

	const submitShareLoan = async (data: UserRole[]) => {
		try {
			setIsModalSubmitting(true)

			await Promise.all(
				data.map((sharedUser) =>
					addSharedUserMutation.mutateAsync({
						userId: sharedUser.user.id,
						loanRole: sharedUser.assignedRole as LoanRole,
						showToast: false,
					})
				)
			)

			await refetch?.()

			alert(formatMessage({ id: 'alerts.share_loan_success' }), {
				severity: 'success',
			})
		} catch (error) {
			captureException(error)
			alert(formatMessage({ id: 'alerts.share_loan_failed' }), {
				severity: 'error',
			})
		} finally {
			setIsModalSubmitting(false)
		}
	}

	const submitRevokeUserAccess = async () => {
		setIsModalSubmitting(true)

		try {
			if (!userToRevoke) return

			await revokeSharedUserMutation.mutateAsync({
				userId: userToRevoke?.id,
			})

			await refetch?.()

			alert(
				formatMessage(
					{ id: 'alerts.revoke_loan_success' },
					{ name: userToRevoke?.name }
				),
				{
					severity: 'success',
				}
			)
		} catch (error) {
			captureException(error)
			alert(
				formatMessage(
					{ id: 'alerts.revoke_loan_failed' },
					{ name: userToRevoke?.name }
				),
				{
					severity: 'error',
				}
			)
		} finally {
			setIsModalSubmitting(false)
			setIsRevokeModalVisible(false)
		}
	}

	const handleEditRoleRow = (row: LoanUserRow) =>
		setEditingRows((prev) => {
			const newMap = new Map(prev)
			newMap.set(row.id, { role: row.role, loading: false })
			return newMap
		})

	const handleInviteClick = async (row: LoanUserRow) => {
		try {
			await sendInviteReminderMutation.mutateAsync(row.id)

			alert(formatMessage({ id: 'alerts.invites_success' }))
		} catch (error) {
			captureException(error)

			alert(formatMessage({ id: 'alerts.invites_failed' }), {
				severity: 'error',
			})
		}
	}

	const columns: TableColumn<LoanUserRow>[] = [
		{
			name: formatMessage({ id: 'global.name' }),
			selector: (row) => row.name,
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.email' }),
			selector: (row) => row.email || '',
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.phone_number' }),
			selector: (row) => row.phone ?? '',
			cell: (row) => applyMask(row.phone, 'phone'),
		},
		{
			name: formatMessage({ id: 'global.role' }),
			selector: (row) => row.userRole ?? '',
			cell: (row) => addSpacesToPascalCase(row.userRole ?? ''),
		},
		{
			name: formatMessage({ id: 'share_loan.assigned_role' }),
			minWidth: '290px',
			selector: (row) => row.role,
			cell: (row) => (
				<LoanUsersTableRoleAssignCell
					row={row}
					editingRows={editingRows}
					onRoleChange={handleRoleChangeRow}
					onRoleSave={handleRoleSaveRow}
					onCancel={handleCancelEditRow}
					showRolesBasedOnUserRole
				/>
			),
		},
		{
			name: formatMessage({ id: 'global.created' }),
			selector: (row) => row.createdAt ?? '',
			cell: (row) => formatDate(row.createdAt, false),
			sortable: true,
			sortField: 'createdAt',
			hide: breakpoint,
		},
	]

	const actionItems: ActionItem<LoanUserRow>[] | undefined =
		isBorrower
			? undefined
			: [
					{
						name: formatMessage({ id: 'invites.invite_reminder' }),
						onClick: (_, row: LoanUserRow) => handleInviteClick(row),
						hideIf: (row: LoanUserRow) =>
							!ALLOWED_ROLES_INVITE.has(row.role),
					},
					{
						name: formatMessage({
							id: 'share_loan.edit_access_loan',
						}),
						onClick: (_e, row: LoanUserRow) => {
							if (!editingRows.has(row.id)) handleEditRoleRow(row)
						},
						hideIf: (row) =>
							editingRows.has(row.id) ||
							!row.shared ||
							row.id === user.id,
					},
					{
						name: formatMessage({
							id: 'share_loan.revoke_access_loan',
						}),
						onClick: (_e, row: LoanUserRow) => handleRevokeClick(row),
						hideIf: (row) => editingRows.has(row.id) || !row.shared,
					},
				]

	return (
		<>
			<DataTable
				keyField="id"
				title={title}
				subHeader={
					<p className="mb-5">
						{formatMessage({ id: 'loans.loan_users_description' })}
					</p>
				}
				data={memoMergedData}
				columns={columns}
				defaultSortAsc={false}
				onRefreshClick={refetch}
				defaultSortFieldId={DEFAULT_SORT_COLUMN}
				progressPending={isLoanLoading && !isLoanRefetching}
				actionItems={actionItems}
				titleActions={[
					<LoanUsersTablesActionsButton
						key="actions-button"
						handleOpenShareLoanModal={handleOpenShareLoanModal}
					/>,
				]}
			/>

			{isLoanAdmin && (
				<>
					<ModalShareLoan
						visible={isShareLoanModalVisible}
						onClose={handleCloseShareLoanModal}
						addUser={{
							onBeforeAdd: handleValidateUserBeforeAdd,
						}}
						submit={{
							onSubmit: submitShareLoan,
							isLoading: isModalSubmitting,
						}}
					/>

					<ModalShareLoanRevoke
						visible={isRevokeModalVisible}
						onClose={handleCloseRevokeModal}
						onSubmit={submitRevokeUserAccess}
						submitting={isModalSubmitting}
						user={userToRevoke}
					/>
				</>
			)}
		</>
	)
}

export default LoanUsersTable
